'use client';

import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useEffect, useRef } from 'react';

interface ScrollAnimationProviderProps {
  children: React.ReactNode;
  deviceType: EPlatform;
}

// 检查元素是否在视口中
function isInViewport(element: Element): boolean {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

export default function ScrollAnimationProvider({
  children,
  deviceType,
}: ScrollAnimationProviderProps) {
  const alreadyAnimationRef = useRef(false);

  // App相关状态
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);

  // 在App内嵌环境时初始化通信
  useEffect(() => {
    if (inAppH5) {
      handleAppCommunication();
    }
  }, [inAppH5]);

  // 处理App通信逻辑
  const handleAppCommunication = async () => {
    try {
      // 清空当前用户信息
      setToken('');
      setUserInfo({});

      // 注册token回调通信
      appCommunicationManager.registerCallback(
        EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
        async (token) => {
          logger.log('App登录token:', token);
          if (token !== 'ClearToken') {
            setToken(token);
            // 获取用户信息
            await refreshAccountInfo();
          }
        },
        {},
        'BackTokenInfo' as any, // 使用BackTokenInfo的postMessage方法
      );
    } catch (error) {
      logger.error('App通信初始化失败:', error);
    }
  };

  // 组件销毁时清理App通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  useEffect(() => {
    // 只在PC端启用动画
    if (deviceType !== EPlatform.WEB) {
      return;
    }

    const handleScroll = () => {
      // Banner2 动画
      const banner2_image = document.querySelector('.candy-glasses-wrap .banner2 .banner2_image');
      const banner2_image2 = document.querySelector('.candy-glasses-wrap .banner2 .banner2_image2');
      const banner2_image3 = document.querySelector('.candy-glasses-wrap .banner2 .banner2_image3');
      const banner2_image4 = document.querySelector('.candy-glasses-wrap .banner2 .banner2_image4');

      if (banner2_image && isInViewport(banner2_image)) {
        banner2_image.classList.add('fadeInUpAnimate');
      }
      if (banner2_image2 && isInViewport(banner2_image2)) {
        setTimeout(() => {
          banner2_image2.classList.add('fadeInUpAnimate');
        }, 500);
      }
      if (banner2_image3 && isInViewport(banner2_image3)) {
        setTimeout(() => {
          banner2_image3.classList.add('fadeInUpAnimate');
        }, 1000);
      }
      if (banner2_image4 && isInViewport(banner2_image4)) {
        banner2_image4.classList.add('fadeInUpAnimate');
      }

      // Banner3 动画
      const banner3_image = document.querySelector('.candy-glasses-wrap .banner3 .banner3_image');
      const banner3_image_anchor = document.querySelector(
        '.candy-glasses-wrap .banner3 .banner3_image_anchor',
      );
      const text_h3 = document.querySelector('.candy-glasses-wrap .banner3 h3');
      const text = document.querySelector('.candy-glasses-wrap .banner3 .text');

      if (banner3_image_anchor && isInViewport(banner3_image_anchor) && banner3_image) {
        banner3_image.classList.add('fadeInUpAnimate');
      }
      if (text_h3 && isInViewport(text_h3)) {
        text_h3.classList.add('fadeInUpAnimate');
      }
      if (text && isInViewport(text)) {
        setTimeout(() => {
          text.classList.add('fadeInUpAnimate');
        }, 500);
      }

      // 照片墙动画
      const photo_wall_title = document.querySelector('.candy-glasses-wrap .photo-list .title');
      const photo_wrapper_anchor = document.querySelector(
        '.candy-glasses-wrap .photo-list .photo_wrapper_anchor',
      );
      const photo_swiper_list = document.querySelectorAll(
        '.candy-glasses-wrap .photo-list .photo-swiper .photo-item',
      );

      if (photo_wall_title && isInViewport(photo_wall_title)) {
        photo_wall_title.classList.add('fadeInUpAnimate');
      }
      if (photo_wrapper_anchor && isInViewport(photo_wrapper_anchor)) {
        if (!alreadyAnimationRef.current) {
          photo_swiper_list.forEach((el, index) => {
            setTimeout(
              () => {
                el.classList.add('fadeInRightPhotoWallAnimate');
              },
              100 * (index + 1),
            );
          });
          alreadyAnimationRef.current = true;

          // 3秒后移除动画类，防止后续循环中的slide有动画
          setTimeout(() => {
            photo_swiper_list.forEach((el) => {
              el.classList.remove('fadeInRightPhotoWallAnimate', 'opacity0');
            });
          }, 3000);
        }
      }

      // 商品列表动画
      const candy_product_list_anchor = document.querySelector(
        '.candy-glasses-wrap .product-list .candy_product_list_anchor',
      );
      const candy_product_list = document.querySelector('.candy-glasses-wrap .product-list ul');

      if (
        candy_product_list_anchor &&
        isInViewport(candy_product_list_anchor) &&
        candy_product_list
      ) {
        setTimeout(() => {
          candy_product_list.classList.add('fadeInUpAnimate');
        }, 500);
      }

      // Banner4 动画
      const bottom_banner_image1 = document.querySelector(
        '.candy-glasses-wrap .bottom-banner .banner_image',
      );
      const bottom_banner_title = document.querySelector('.candy-glasses-wrap .bottom-banner h5');
      const bottom_banner_sub1 = document.querySelector(
        '.candy-glasses-wrap .bottom-banner .sub_banners1',
      );
      const bottom_banner_sub2 = document.querySelector(
        '.candy-glasses-wrap .bottom-banner .sub_banners2',
      );
      const bottom_banner_sub3 = document.querySelector(
        '.candy-glasses-wrap .bottom-banner .sub_banners3',
      );

      if (bottom_banner_image1 && isInViewport(bottom_banner_image1)) {
        bottom_banner_image1.classList.add('fadeInUpAnimate');
      }
      if (bottom_banner_title && isInViewport(bottom_banner_title)) {
        setTimeout(() => {
          bottom_banner_title.classList.add('fadeInUpAnimate');
        }, 500);
      }
      if (bottom_banner_sub1 && isInViewport(bottom_banner_sub1)) {
        bottom_banner_sub1.classList.add('fadeInUpAnimate');
      }
      if (bottom_banner_sub2 && isInViewport(bottom_banner_sub2)) {
        bottom_banner_sub2.classList.add('fadeInUpAnimate');
      }
      if (bottom_banner_sub3 && isInViewport(bottom_banner_sub3)) {
        bottom_banner_sub3.classList.add('fadeInUpAnimate');
      }
    };

    // 添加滚动监听
    window.addEventListener('scroll', handleScroll, true);

    // 初始检查
    handleScroll();

    // 清理函数
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [deviceType]);

  return <>{children}</>;
}
